/**
 * Cliente con predicción + reconciliación + interpolación (M1).
 *
 * - Predicción: aplica el input local de inmediato → input lag percibido ≈ 0.
 * - Reconciliación: al llegar un snapshot autoritativo, fija ese estado y
 *   re-simula los inputs locales aún no confirmados. Como `@tak/sim` es
 *   determinista, el replay == lo que el servidor calculará (sin rubber-banding
 *   salvo corrección real). El error de misprediction NO se teletransporta: se
 *   guarda como un offset de render que decae en unos ticks (position error
 *   smoothing), de modo que el disco local nunca "salta".
 * - Interpolación: entidades remotas (balón y rival) se renderizan "en el
 *   pasado" desde un buffer de snapshots, con extrapolación corta cuando el
 *   buffer se agota (evita congelamiento/teleport con jitter).
 * - Reloj adaptativo: el lead sobre el servidor se dimensiona con la señal
 *   `bufAhead` del snapshot (jitter-buffer) y se corrige de forma GRADUAL
 *   (±1 tick por snapshot) salvo saltos grandes (join / hitch).
 */
import { JitterEstimator } from "./jitter";
import {
  GameState, InputState, MapConfig, MatchRules, TeamId,
  createGame, createInputState, updateGameMulti, mapById, PLAYER_ACCEL_FORCE,
  setActiveSlots, POSSESSION_RANGE, POSSESSION_TIME_MAX, BALL_R, KICK_SPEED_MIN,
} from "../game";
import {
  InputCommand, Snapshot, BodyState, NET_FIXED_DT,
  SNAPSHOT_INTERVAL_TICKS, MAX_INPUT_LEAD,
  writeBody, cloneInput, lerpBody,
} from "./protocol";

export interface ClientOptions {
  players?: number;
  /** Slot que controla este cliente (0..N-1). Por defecto 0. */
  localSlot?: number;
  /** Equipo por slot (rojo/turquesa); por defecto alterna. */
  teams?: TeamId[];
  map?: MapConfig;
  rules?: MatchRules;
  /**
   * Si `true`, el cliente mantiene su reloj POR DELANTE del servidor a partir de
   * los snapshots (ver `bufAhead`). Imprescindible en red real: sin esto, el
   * reloj local arranca en 0 y, con latencia, los inputs llegan con
   * `cmd.tick < server.tick` y el servidor los rechaza por tardíos → ningún
   * disco se mueve en la simulación autoritativa. El loopback in-process NO lo
   * necesita (ya atrasa el servidor), así que por defecto está desactivado para
   * no alterar tests deterministas.
   */
  autoLead?: boolean;
  /**
   * Si `true`, el mundo predicho nace con **un solo slot ocupado** (el local) en
   * vez de con todos.
   *
   * Es lo que evita los discos de relleno al entrar a una sala: hasta que llega
   * el primer snapshot con la ocupación real, un cliente que nace con los N
   * slots activos dibuja (y simula) N−1 discos parados en su saque que no son
   * nadie. Duraba décimas de segundo, pero es exactamente el "señuelo" que no
   * debe existir — en una sala 1v0 se veía un rival que no había.
   *
   * Por defecto `false`: el loopback y los tests construyen snapshots sin
   * `active` y esperan el comportamiento histórico (todos dentro).
   */
  soloStart?: boolean;
}

/** Patada de un RIVAL detectada al reconciliar (para sonido y partículas). */
export interface RemoteKick {
  slot: number;
  /** Punto del impacto (borde del balón, del lado del que patea). */
  x: number;
  y: number;
  /** Normal de la patada (dirección en la que sale el balón). */
  nx: number;
  ny: number;
  /** 0..1, como `KickInfo.intensity` del sim local. */
  intensity: number;
}

interface Pending {
  tick: number;
  input: InputState;
}

const IDLE: InputState = createInputState();
const MAX_SNAP_BUFFER = 32;
/** Cola vacía compartida: `takeRemoteKicks` se llama en cada frame. */
const EMPTY_KICKS: RemoteKick[] = [];

/* ----------------------------- reloj adaptativo ---------------------------- */
/** Lead inicial (ticks) al sincronizar por primera vez, antes de medir la red. */
const INITIAL_LEAD = 8;
/** Lead mínimo/máximo permitido (el máx deja holgura bajo `MAX_INPUT_LEAD`). */
const MIN_LEAD = 2;
/**
 * El lead NO está limitado por la ventana del servidor, y la cuenta es
 * contraintuitiva: esa ventana se mide desde el reloj del servidor CUANDO LLEGA
 * el input, no desde el snapshot que recibió el cliente. Con latencia L el
 * input aterriza 2L ticks después del instante que describía el snapshot, así
 * que un lead de 2L+ε cae dentro de la ventana por grande que sea 2L. Limitar
 * el lead a la ventana (que suena prudente) es justo lo que deja al jugador sin
 * poder moverse en conexiones lentas. Quien vigila el borde superior es
 * `bufAhead`, que el servidor mide y el cliente persigue.
 */
const MAX_LEAD = MAX_INPUT_LEAD - 4;

/**
 * Cada cuántos ticks se prueba otro lead cuando el servidor no confirma NADA.
 *
 * Sin confirmaciones el cliente no puede saber por qué le rechazan los inputs:
 * puede ir demasiado tarde (lead corto) o demasiado adelantado (lead pasado de
 * la ventana del servidor). El controlador normal solo sabe subir el lead, así
 * que ante el segundo caso empeora el problema y no sale nunca. Barriendo la
 * ventana se prueban las dos hipótesis y se sale en menos de dos segundos.
 */
const RECOVERY_INTERVAL_TICKS = 24;
/** Inputs que queremos ver encolados por delante en el servidor (jitter-buffer). */
const TARGET_BUFFER = 2;
/** Salto duro del reloj si la deriva supera esto (join / hitch de red). */
const CLOCK_SNAP_THRESHOLD = 8;
/**
 * Retroceso del reloj del servidor (ticks) que ya no puede explicarse por
 * reordenamiento de paquetes y solo cabe leer como "sesión nueva" (revancha,
 * reinicio del host, servidor reiniciado). 1 segundo a 60 Hz: el reordenamiento
 * real vive en el orden de unos pocos ticks.
 */
const SESSION_RESET_TICKS = 60;

/**
 * Vida media (ticks) de la aceleración observada del rival.
 *
 * Al rival no se le puede preguntar qué teclas pulsa, pero sus dos últimos
 * snapshots dicen cómo cambió su velocidad, y esa aceleración es información
 * real: sin ella el rival solo conserva su velocidad y el rozamiento lo va
 * frenando, así que se dibuja sistemáticamente por detrás de donde está.
 *
 * Lo que NO se puede es creérsela indefinidamente. Extrapolar aceleración es de
 * segundo orden: el error crece con el CUADRADO del horizonte, y el horizonte
 * aquí es la latencia. Aplicada tal cual, en cuanto el rival cambia de dirección
 * la extrapolación sigue acelerando hacia donde ya no va, el error se sale del
 * techo de suavizado y la corrección entra de golpe — exactamente el salto que
 * se quería quitar (medido: 118 px en un frame a 334 ms de RTT, 218 px a 468).
 *
 * Por eso se acota por los dos lados: en magnitud, a lo que el motor de un disco
 * puede dar (un choque o un saque producen Δv que no son "el rival acelerando"),
 * y en el tiempo, con esta vida media corta — vale para el instante en que se
 * midió, no para siempre. Con 3 ticks la extrapolación mejora a la inercia pura
 * en salto Y en desfase a la vez, de 67 a 668 ms de RTT; a partir de ~4 empieza
 * a comprar desfase pagando saltos, que es el mal negocio de siempre.
 */
const REMOTE_ACC_HALFLIFE = 3;

/* --------------------------- suavizado de error ---------------------------- */
/**
 * Factor de decaimiento exponencial del offset de corrección por tick.
 * 0.85 ⇒ el 15% del error restante se reabsorbe cada tick.
 */
const SMOOTH_DECAY = 0.85;

/**
 * Techo de error que se suaviza, en **tiempo de vuelo** en vez de en píxeles
 * fijos.
 *
 * El error de predicción legítimo es proporcional a lo que el cuerpo se mueve
 * mientras el snapshot viaja: un balón recién pateado a 1900 px/s acumula
 * cientos de píxeles de diferencia en 150 ms, y un disco a 360 px/s apenas
 * cincuenta. Con un tope fijo en píxeles, las correcciones rápidas — que son
 * las NORMALES tras una patada — caían del lado "teletransporta", y eso es el
 * salto brusco que se ve. Escalándolo con la velocidad, la misma situación se
 * lee como lo que es (una corrección esperable) y se resbala; lo que sigue
 * aplicándose de golpe es el teletransporte de verdad: el saque tras un gol,
 * donde el cuerpo está QUIETO y aun así aparece a media cancha.
 */
const SMOOTH_SPEED_WINDOW = 0.20; // 200 ms de vuelo
/** Suelo del techo anterior (px), para cuerpos casi parados. */
const SMOOTH_BASE_LOCAL = 45;
const SMOOTH_BASE_BALL = 60;
const SMOOTH_BASE_PLAYER = 60;

/**
 * Multiplicador del techo de suavizado del balón **mientras lo estás tocando tú**.
 *
 * Cuando el balón lo mueve tu propio disco, la predicción local es la que manda
 * y el servidor va a confirmar casi exactamente lo mismo: la diferencia que
 * llega en el snapshot es el desfase de la red, no una jugada distinta. Sin
 * este margen extra, una corrección normal de ese tramo se pasaba del techo y
 * se aplicaba DE GOLPE — el tirón que se ve justo al conducir o al patear, que
 * es el peor momento posible para que la pelota dé un salto.
 *
 * Cuando quien la toca es el rival, el techo NO se amplía: ahí la corrección sí
 * puede ser una jugada que no habíamos predicho (su patada), y arrastrarla poco
 * a poco sería enseñar una pelota que ya no está donde se dibuja.
 */
const SMOOTH_LOCAL_TOUCH_FACTOR = 3.5;
/**
 * Ticks que dura ese margen tras el último contacto local (~0,4 s). Cubre el
 * viaje de ida y vuelta del snapshot que confirma la patada, que es justo el
 * tramo en el que la corrección llega grande.
 */
const LOCAL_TOUCH_TICKS = 24;

/* --------------------- patadas del rival (sonido/efectos) ------------------ */
/**
 * Salto de rapidez del balón (px/s) entre lo predicho y lo autoritativo que ya
 * no se explica por un rebote: es alguien pateando.
 *
 * El cliente no simula las teclas del rival, así que su patada no existe en la
 * predicción — llega como un cambio brusco en el snapshot. Detectarla es lo que
 * permite que el rival SUENE y levante partículas como el jugador local. Se
 * exige además que haya un rival al alcance del balón, para no confundir un
 * saque tras gol o una corrección de red con una patada.
 */
const REMOTE_KICK_DELTA = KICK_SPEED_MIN * 0.6;
/** Holgura sobre el alcance de patada para atribuirla a un rival. */
const REMOTE_KICK_RANGE_PAD = 26;

/**
 * Velocidad máxima (px/s) a la que se reabsorbe el error.
 *
 * El decaimiento exponencial por sí solo es proporcional al error: con un
 * offset de 300 px, el PRIMER tick ya mueve 45 px en 16 ms — un latigazo. El
 * techo de velocidad reparte esa corrección en varios ticks, de modo que el
 * cuerpo "curva" hacia su posición autoritativa en vez de dar un tirón. Los
 * errores pequeños siguen resolviéndose por la exponencial (más rápida que
 * este techo), así que la respuesta inmediata no se pierde.
 */
const CORRECTION_MIN_SPEED = 420;
/** …y proporcional a la propia velocidad del cuerpo, para que la corrección de
 *  un balón rapidísimo no se eternice (se nota como "flotar"). */
const CORRECTION_SPEED_RATIO = 0.6;
/** Por debajo de esto el offset se considera cero (evita colas infinitas). */
const SMOOTH_EPS = 0.05;

/** Techo de error suavizable de un cuerpo que va a `speed` px/s. */
function smoothCap(base: number, speed: number): number {
  return base + speed * SMOOTH_SPEED_WINDOW;
}

/** Rapidez (px/s) de un cuerpo del snapshot. */
function bodySpeed(b: BodyState): number {
  return Math.hypot(b.vx, b.vy);
}

/* ---------------------------- interpolación -------------------------------- */
/** Delay mínimo de interpolación (ticks) para las entidades interpoladas en el
 *  pasado (rivales). Con red estable (jitter ~0) se rinde casi en el presente
 *  (2 ticks ≈ 33 ms); sube con el jitter medido para no reintroducir tirones. */
const INTERP_MIN_DELAY = 2;
const INTERP_MAX_DELAY = 12;
/** Extrapolación máxima cuando el buffer se agota (ticks). ~130 ms a 60 Hz. */
const MAX_EXTRAP_TICKS = 8;

/* ------------------------------- ping/RTT ---------------------------------- */
/** ms por tick de red (para expresar el RTT medido en milisegundos). */
const MS_PER_TICK = NET_FIXED_DT * 1000;
/** Suavizado EWMA del RTT (0.1 ⇒ ~10 muestras de constante de tiempo). */
const RTT_EWMA = 0.1;
/**
 * Ticks prediciendo sin que el servidor confirme NINGÚN input antes de dar la
 * voz de alarma (1,5 s). Por encima de eso ya no es jitter: o los inputs llegan
 * tarde y se descartan, o el enlace de subida está roto. En ambos casos el
 * jugador se está moviendo solo en su pantalla.
 */
const INPUT_STALL_TICKS = 90;

export class PredictiveClient {
  /** Estado predicho que se renderiza. */
  readonly game: GameState;
  /**
   * Sesgo (ticks) sobre el delay de interpolación adaptativo, elegido por el
   * jugador en los ajustes (ver `interpDelayTicks`).
   *
   * Positivo = más colchón: los rivales se ven más suaves y un poco más en el
   * pasado. Negativo = más cerca del presente, a costa de extrapolar más y de
   * que alguna corrección se note. Cero deja mandar solo al jitter medido.
   */
  interpBias = 0;
  /** Nº de cuerpos del mundo. Mutable: el servidor puede indicar la capacidad
   *  real de la sala en el `welcome` (ver `configurePlayers`). */
  numPlayers: number;
  /** Slot que controla este cliente. El servidor puede reasignarlo por `welcome`
   *  (matchmaking) antes de que empiece a predecir. */
  localSlot: number;
  private tick = 0;
  private pending: Pending[] = [];
  private snapBuffer: Snapshot[] = [];
  private lastServerTick = -1;
  private readonly autoLead: boolean;

  /** Lead adaptativo (ticks por delante del servidor). Sube/baja con `bufAhead`. */
  private leadTarget = INITIAL_LEAD;
  private bufAheadEwma = TARGET_BUFFER;
  private bufJitterEwma = 0;
  /** Jitter de llegada de snapshots (para dimensionar el delay de interpolación). */
  private offsetEwma = 0;
  private offsetJitterEwma = 0;
  private syncedOffset = false;

  /** Offset de render del disco local (position error smoothing). No autoritativo:
   *  solo desplaza el DIBUJO para que la corrección no se vea como un teleport. */
  private errX = 0;
  private errY = 0;
  /** Offset de render del BALÓN (mismo mecanismo que el disco local). El balón se
   *  DIBUJA en su posición predicha + este offset que decae, en vez de interpolarse
   *  en el pasado → responde en el presente (a tus propios toques sin latencia) y
   *  las correcciones autoritativas "resbalan" sin saltar. */
  private errBallX = 0;
  private errBallY = 0;
  /** Offset de render de cada RIVAL (por slot; el local no se usa). Mismo mecanismo
   *  que el balón: se dibuja al rival en su posición PREDICHA + este offset que
   *  decae, en el plano PRESENTE (no interpolado en el pasado), para que la pelota
   *  interactúe con él donde se ve. */
  private errPlayerX: number[] = [];
  private errPlayerY: number[] = [];
  /** Aceleración observada de cada rival entre los dos últimos snapshots
   *  (px/s²), acotada y con caducidad. Ver `REMOTE_ACC_HALFLIFE`. */
  private accX: number[] = [];
  private accY: number[] = [];
  /** Velocidad del rival en el snapshot anterior, para derivar la aceleración. */
  private prevVx: number[] = [];
  private prevVy: number[] = [];
  private prevSnapTick = -1;
  /** Ticks extrapolados desde el último snapshot (edad de `accX/accY`). */
  private ticksSinceSnap = 0;

  /** RTT estimado (ticks) suavizado, y su máximo (ms), para el visualizador de
   *  ping. Se deriva de `this.tick - snap.ackTick` (edad del último input
   *  confirmado) descontando el intervalo de snapshot (artefacto de muestreo). */
  private rttTicksEwma = 0;
  /** Estabilidad del RTT (ver `jitterMs`): sube con el pico, baja despacio. */
  private readonly rttJitter = new JitterEstimator();
  private rttSynced = false;
  private rttMaxMs = 0;
  /**
   * Primer tick de input cuyo ACK sirve para medir RTT. Se re-fija cada vez que
   * el reloj local pega un salto (join, hitch, vuelta de una pestaña en segundo
   * plano): las medidas anteriores a ese salto miden el salto, no la red.
   */
  private rttValidFromTick = 0;
  /**
   * Objetos reutilizados para devolver los offsets de render. El render los lee
   * y los usa en el acto, una vez por frame; crear un objeto nuevo en cada
   * acceso llenaba el heap de basura de vida cortísima y esa factura se paga en
   * pausas del recolector, que es lo que el jugador ve como un bajón de fps.
   */
  private readonly scratchLocal = { x: 0, y: 0 };
  /** Ver `soloStart`: el mundo nace con un solo slot ocupado (el local). */
  private readonly soloStart: boolean;
  /** ¿Algún snapshot trajo ocupación de slots? (ver `serverOutdated`). */
  private sawActive = false;
  /** ¿Algún snapshot llegó SIN ocupación de slots? (ver `serverOutdated`). */
  private sawLegacy = false;
  /**
   * Ticks que quedan de "margen de toque local" para el balón (ver
   * `SMOOTH_LOCAL_TOUCH_FACTOR`). Se rearma mientras el disco local tiene la
   * pelota al alcance y va bajando tick a tick.
   */
  private localTouchTicks = 0;
  /** Patadas de rivales detectadas y aún no drenadas (ver `takeRemoteKicks`). */
  private remoteKicks: RemoteKick[] = [];
  private readonly scratchBall = { x: 0, y: 0 };
  private readonly scratchPlayer = { x: 0, y: 0 };
  /** Paso actual del barrido de recuperación (ver RECOVERY_INTERVAL_TICKS). */
  private recoveryStep = 0;
  /** Ticks predichos desde el último input confirmado por el servidor. */
  private ticksSinceAck = 0;
  private lastAckSeen = -1;
  /**
   * Cuántas veces se ha llamado a `predict()`, y cuántas llevábamos en el
   * snapshot anterior. La diferencia responde a "¿está el cliente jugando?".
   *
   * Es la señal que distingue una red mala de un cliente parado. Cuando el
   * navegador congela `requestAnimationFrame` (pestaña en segundo plano) el
   * cliente deja de predecir y de enviar inputs, pero los snapshots siguen
   * llegando: sin esta distinción, el netcode lee esa ausencia como "voy corto
   * de buffer y con latencia altísima" y reacciona a un problema que no existe.
   */
  private predictCount = 0;
  private predictCountAtLastSnap = 0;
  /**
   * true mientras el controlador del jitter-buffer arrastra historia de un
   * periodo en el que el cliente no estaba jugando. Se limpia con el primer
   * snapshot que confirme un input NUESTRO posterior a la vuelta: hasta
   * entonces, `bufAhead` sigue describiendo la ausencia (el servidor aún no ha
   * visto nada nuevo nuestro) y alimentarlo dispararía el lead al máximo justo
   * al volver a la pestaña.
   */
  private bufControlStale = false;

  constructor(opts: ClientOptions = {}) {
    this.numPlayers = Math.max(1, Math.floor(opts.players ?? 1));
    this.localSlot = opts.localSlot ?? 0;
    this.autoLead = opts.autoLead ?? false;
    this.soloStart = opts.soloStart ?? false;
    this.game = createGame(opts.map, opts.rules, {
      players: this.numPlayers, teams: opts.teams, active: this.initialActive(),
    });
    this.errPlayerX = new Array(this.numPlayers).fill(0);
    this.errPlayerY = new Array(this.numPlayers).fill(0);
  }

  /**
   * Ocupación con la que nace (o renace) el mundo predicho. Con `soloStart`,
   * solo el slot local: nadie más existe hasta que el servidor diga lo contrario
   * en el primer snapshot. Sin él, `undefined` = todos (comportamiento histórico).
   */
  private initialActive(): boolean[] | undefined {
    if (!this.soloStart) return undefined;
    return Array.from({ length: this.numPlayers }, (_, slot) => slot === this.localSlot);
  }

  get currentTick(): number {
    return this.tick;
  }

  /**
   * Reconfigura la cancha/reglas de la sala (mapa elegido por el host). Muta el
   * GameState EN SITIO (`Object.assign`) para no romper referencias externas
   * (main renderiza `client.game`), y reinicia el estado predicho.
   */
  reconfigure(map: MapConfig, rules: MatchRules, teams?: TeamId[]): void {
    // La ocupación se conserva si ya la sabíamos (cambio de mapa a mitad de
    // sala): recrear el mundo "lleno" repoblaría la cancha de discos que no son
    // nadie hasta el siguiente snapshot.
    const active = this.game.active.length === this.numPlayers
      ? this.game.active.slice()
      : this.initialActive();
    Object.assign(this.game, createGame(map, rules, { players: this.numPlayers, teams, active }));
    this.resync();
  }

  /**
   * Reinicia SOLO la contabilidad de red/predicción, CONSERVANDO el mundo que se
   * está renderizando. Es lo que hace falta tras una **reconexión** (UX-2/NET-1):
   * el socket nuevo trae otro reloj de servidor, así que hay que olvidar el tick
   * local, los inputs pendientes y el buffer de snapshots — pero no borrar la
   * cancha, que se sigue viendo hasta que llegue el primer snapshot y la corrija.
   *
   * Sin esto, `reconcile` descartaría los snapshots del socket nuevo por
   * "viejos" (`serverTick <= lastServerTick`) y la partida quedaría congelada.
   */
  resync(): void {
    this.tick = 0;
    this.pending = [];
    this.snapBuffer = [];
    this.lastServerTick = -1;
    this.leadTarget = INITIAL_LEAD;
    this.bufAheadEwma = TARGET_BUFFER;
    this.bufJitterEwma = 0;
    this.syncedOffset = false;
    this.errX = 0;
    this.errY = 0;
    this.errBallX = 0;
    this.errBallY = 0;
    this.errPlayerX = new Array(this.numPlayers).fill(0);
    this.errPlayerY = new Array(this.numPlayers).fill(0);
    // La aceleración medida describe la sesión ANTERIOR: arrastrarla lanzaría a
    // los rivales en la dirección que llevaban antes del reinicio.
    this.accX = new Array(this.numPlayers).fill(0);
    this.accY = new Array(this.numPlayers).fill(0);
    this.prevVx = new Array(this.numPlayers).fill(0);
    this.prevVy = new Array(this.numPlayers).fill(0);
    this.prevSnapTick = -1;
    this.ticksSinceSnap = 0;
    this.rttTicksEwma = 0;
    this.rttJitter.reset();
    this.rttSynced = false;
    this.rttValidFromTick = 0;
    this.ticksSinceAck = 0;
    this.lastAckSeen = -1;
    this.recoveryStep = 0;
    this.localTouchTicks = 0;
    // Las patadas pendientes describen la sesión anterior: sonarían fuera de sitio.
    this.remoteKicks.length = 0;
    // rttMaxMs se conserva a propósito: es el máximo de la SESIÓN, no del mapa.
  }

  /**
   * Ajusta el mundo a `players` cuerpos (capacidad real de la sala, que el
   * servidor comunica en el `welcome`). Reconstruye el GameState conservando
   * mapa/reglas y reinicia el estado predicho. No-op si ya coincide.
   */
  configurePlayers(players: number, teams?: TeamId[]): void {
    const n = Math.max(1, Math.floor(players));
    if (n === this.numPlayers && !teams) return;
    this.numPlayers = n;
    const g = this.game;
    // El mundo cambia de tamaño: la ocupación anterior ya no describe nada, así
    // que se parte de la inicial (con `soloStart`, solo tú).
    Object.assign(g, createGame(mapById(g.mapId), { goalLimit: g.rules.goalLimit, timeLimit: g.rules.timeLimit }, {
      players: n, teams, active: this.initialActive(),
    }));
    this.resync();
  }

  get pendingCount(): number {
    return this.pending.length;
  }

  /** serverTick del snapshot más reciente (o -1). Base del reloj de interpolación. */
  get latestServerTick(): number {
    return this.snapBuffer.length ? this.snapBuffer[this.snapBuffer.length - 1].serverTick : -1;
  }

  /** Índice del cuerpo que controla este cliente. */
  private get localBody(): number {
    return this.bodyOf(this.localSlot);
  }

  private bodyOf(slot: number): number {
    return this.game.players[slot] ?? -1;
  }

  /**
   * Aplica el input local. A los rivales no se les inventa ninguna tecla:
   * conservan su velocidad autoritativa (con el rozamiento del mundo) más su
   * aceleración observada, acotada y con caducidad (`REMOTE_ACC_HALFLIFE`).
   *
   * Antes se hacía dead reckoning: traducir su velocidad a teclas y seguir
   * empujándolos. Es sobre-predicción — cuando el rival cambiaba de dirección la
   * predicción aceleraba hacia donde ya no iba y la corrección entraba de golpe
   * (saltos de 105 px en un frame a 334 ms de RTT, 19× lo que salta el disco
   * propio). La aceleración medida es distinta: es un dato observado, no una
   * suposición sobre sus intenciones, y acotada mejora a la inercia pura en
   * salto y en desfase a la vez.
   */
  private applyLocal(input: InputState): void {
    const inputs: InputState[] = [];
    for (let i = 0; i < this.numPlayers; i++) {
      inputs.push(i === this.localSlot ? input : IDLE);
    }
    // La confianza en la aceleración medida decae con el horizonte extrapolado.
    const k = Math.pow(0.5, this.ticksSinceSnap / REMOTE_ACC_HALFLIFE);
    if (k > 0.01) {
      const w = this.game.world;
      for (let i = 0; i < this.numPlayers; i++) {
        if (i === this.localSlot) continue;
        const b = this.bodyOf(i);
        if (b < 0) continue;
        w.vx[b] += (this.accX[i] ?? 0) * k * NET_FIXED_DT;
        w.vy[b] += (this.accY[i] ?? 0) * k * NET_FIXED_DT;
      }
    }
    this.ticksSinceSnap++;
    updateGameMulti(this.game, inputs, NET_FIXED_DT);
  }

  /** Aplica input local ya (predicción) y devuelve el comando a enviar. */
  predict(input: InputState): InputCommand {
    const cmd: InputCommand = { tick: this.tick, input: cloneInput(input) };
    this.applyLocal(input);
    this.pending.push({ tick: this.tick, input: cloneInput(input) });
    this.tick++;
    this.predictCount++;
    this.ticksSinceAck++;
    // Margen de "la estás tocando tú" para el suavizado del balón: se rearma
    // mientras el disco local la tiene al alcance, y se agota solo al soltarla.
    if (this.game.chargings[this.localSlot] === true) this.localTouchTicks = LOCAL_TOUCH_TICKS;
    else if (this.localTouchTicks > 0) this.localTouchTicks--;
    // El offset de corrección se disuelve en tiempo de simulación (determinista,
    // sin acoplar al bucle de render): así el disco local "resbala" al estado
    // autoritativo en vez de teletransportarse.
    this.decaySmoothing();
    return cmd;
  }

  /**
   * Reabsorbe un offset de corrección un tick. Trabaja sobre el VECTOR (no eje
   * a eje) para que la trayectoria de la corrección sea recta, y combina dos
   * límites:
   *  - exponencial: rápido con errores pequeños → respuesta inmediata;
   *  - techo de velocidad: reparte los errores grandes en varios ticks → sin
   *    tirón. El techo sube con la velocidad del propio cuerpo, para que una
   *    corrección grande sobre algo que va muy rápido no se quede flotando.
   */
  /**
   * Factor por el que hay que multiplicar un offset (ex, ey) para reabsorberlo
   * un tick. Devuelve un NÚMERO en vez de un par para no asignar nada: esto
   * corre una vez por cuerpo y por tick, y la basura que genera el camino
   * caliente se paga en pausas del recolector, que es justo lo que se ve como
   * un bajón de fps.
   */
  private decayFactor(ex: number, ey: number, speed: number): number {
    const mag = Math.hypot(ex, ey);
    if (mag < SMOOTH_EPS) return 0;
    const rate = Math.max(CORRECTION_MIN_SPEED, speed * CORRECTION_SPEED_RATIO);
    const step = Math.min(mag * (1 - SMOOTH_DECAY), rate * NET_FIXED_DT);
    const next = mag - step;
    return next < SMOOTH_EPS ? 0 : next / mag;
  }

  private decaySmoothing(): void {
    const w = this.game.world;
    const speedOf = (body: number): number =>
      body >= 0 ? Math.hypot(w.vx[body], w.vy[body]) : 0;

    const kl = this.decayFactor(this.errX, this.errY, speedOf(this.localBody));
    this.errX *= kl;
    this.errY *= kl;
    const kb = this.decayFactor(this.errBallX, this.errBallY, speedOf(this.game.ball));
    this.errBallX *= kb;
    this.errBallY *= kb;
    for (let i = 0; i < this.errPlayerX.length; i++) {
      const k = this.decayFactor(this.errPlayerX[i], this.errPlayerY[i], speedOf(this.bodyOf(i)));
      this.errPlayerX[i] *= k;
      this.errPlayerY[i] *= k;
    }
  }

  /**
   * Offset de render (px) del disco local: súmalo a su posición predicha al
   * DIBUJAR para ocultar la corrección de misprediction. Nunca afecta la
   * simulación (el estado autoritativo sigue siendo el de `game`).
   */
  get localRenderOffset(): { x: number; y: number } {
    this.scratchLocal.x = this.errX;
    this.scratchLocal.y = this.errY;
    return this.scratchLocal;
  }

  /**
   * Offset de render (px) del balón (mismo mecanismo que `localRenderOffset`).
   * Súmalo a la posición PREDICHA del balón al dibujar: así el balón se ve en el
   * presente (sin el retraso de interpolar en el pasado) y las correcciones
   * autoritativas se disuelven sin saltos.
   */
  get ballRenderOffset(): { x: number; y: number } {
    this.scratchBall.x = this.errBallX;
    this.scratchBall.y = this.errBallY;
    return this.scratchBall;
  }

  /**
   * Offset de render (px) de un RIVAL. Súmalo a su posición PREDICHA (coasting) al
   * dibujar: el rival se ve en el plano PRESENTE (mismo que el balón), no atrás en
   * el tiempo, y las correcciones autoritativas se disuelven sin saltos.
   */
  playerRenderOffset(slot: number): { x: number; y: number } {
    this.scratchPlayer.x = this.errPlayerX[slot] ?? 0;
    this.scratchPlayer.y = this.errPlayerY[slot] ?? 0;
    return this.scratchPlayer;
  }

  /** RTT estimado al servidor (ms), suavizado. 0 hasta el primer snapshot. */
  get pingMs(): number {
    return this.rttSynced ? this.rttTicksEwma * MS_PER_TICK : 0;
  }

  /**
   * ¿Hay una medida de ping de verdad? Sin esto, "0 ms" es ambiguo: puede ser
   * una conexión local perfecta o que no se haya podido medir NADA. La UI
   * necesita distinguirlas para no dar por buena una partida rota.
   */
  get hasPing(): boolean {
    return this.rttSynced;
  }

  /**
   * El servidor lleva demasiado sin confirmar ningún input nuestro estando
   * conectados. Es el síntoma de que nuestros inputs llegan tarde y se están
   * descartando: la predicción local se ve fluida, pero en la simulación
   * autoritativa el jugador NO se mueve. Sin exponerlo, el jugador solo ve que
   * "algo va raro" y asume que se ha desconectado.
   */
  get inputStalled(): boolean {
    return this.ticksSinceAck > INPUT_STALL_TICKS;
  }

  /** Máximo RTT observado en la sesión (ms). Para el "peor caso" del visualizador. */
  get pingMaxMs(): number {
    return this.rttMaxMs;
  }

  /**
   * INESTABILIDAD de la conexión en ms: cuánto se mueve el RTT, contando los
   * picos recientes y no solo la media.
   *
   * Es el número que separa "juego lejos" de "mi red va mal", y son problemas
   * distintos: el primero no tiene arreglo y el segundo casi siempre sí.
   */
  get jitterMs(): number {
    return this.rttSynced ? this.rttJitter.headroom * MS_PER_TICK : 0;
  }

  /**
   * Ocupación de slots según el snapshot.
   *
   * `active` es OPCIONAL en el protocolo, así que hay dos servidores posibles:
   * uno que dice quién hay y otro (sin actualizar) que se lo calla. Con
   * `soloStart` el mundo predicho nace con un único slot ocupado —el tuyo—, de
   * modo que contra el segundo el cliente se quedaba ciego para siempre: el
   * rival no se dibujaba, no salía ni como flecha de borde, y sin embargo el
   * servidor sí simulaba su cuerpo. Eso es exactamente lo que se veía en la
   * cancha: alguien invisible con el que chocabas y que devolvía tiros.
   *
   * Cuando el servidor no dice quién hay, se vuelve al comportamiento
   * histórico: todo slot que el snapshot describe está ocupado. Es la
   * suposición segura — de más se dibuja un disco parado que no es nadie, de
   * menos se juega contra un fantasma.
   */
  private applyActive(snap: Snapshot): void {
    if (snap.active) {
      this.sawActive = true;
      setActiveSlots(this.game, snap.active);
      return;
    }
    this.sawLegacy = true;
    setActiveSlots(this.game, this.game.active.map(() => true));
  }

  /**
   * El servidor de esta partida no manda ocupación de slots (protocolo viejo).
   *
   * Se expone para poder DECIRLO: contra un backend así la sala se ve rara —
   * discos parados que no son nadie— y sin explicación parece un bug del juego.
   * Lo es del despliegue, y quien lo opera merece enterarse.
   */
  get serverOutdated(): boolean {
    return this.sawLegacy && !this.sawActive;
  }

  /** Fija el estado autoritativo y re-simula los inputs locales no confirmados. */
  reconcile(snap: Snapshot): void {
    // El snapshot describe MÁS cuerpos de los que tiene el mundo predicho (un
    // `welcome` perdido, una sala que creció). Los slots que faltan no existen
    // en local: no se dibujan, no se interpolan, y sus choques llegan como
    // empujones de la nada. El mundo se agranda para caber lo que hay.
    if (snap.players.length > this.numPlayers) this.configurePlayers(snap.players.length);
    // El reloj del servidor dio un salto ATRÁS enorme: no es reordenamiento de
    // red (eso son unos pocos ticks), es una SESIÓN NUEVA — el host pidió
    // revancha/reinicio y la sala recreó su simulación desde el tick 0, o el
    // proceso del servidor se reinició. Sin esto, todos los snapshots nuevos
    // parecerían "viejos", se descartarían para siempre y la partida quedaría
    // congelada justo al pulsar REVANCHA.
    if (this.lastServerTick - snap.serverTick > SESSION_RESET_TICKS) this.resync();
    // Transporte no confiable (UDP): descarta snapshots viejos o reordenados.
    // El más nuevo siempre gana; los rezagados no deben "retroceder" el estado.
    else if (snap.serverTick < this.lastServerTick) return;
    else if (snap.serverTick === this.lastServerTick) {
      // MISMO tick que el anterior. No es un duplicado inútil: el reloj del
      // servidor SOLO avanza mientras la sala juega, así que todo lo que ocurre
      // con la partida parada —el anfitrión pulsa FINALIZAR estando en pausa—
      // llega en snapshots con el tick clavado. Descartándolos, el cliente no se
      // enteraba nunca de que el partido había terminado: la sala pasaba a
      // "over" y la pantalla de resultado no aparecía jamás. Los cuerpos no se
      // tocan (no se han movido); lo que sí se adopta es el veredicto.
      this.adoptMatchState(snap);
      return;
    }
    const firstSync = this.lastServerTick < 0;
    this.lastServerTick = snap.serverTick;

    // Qué slots tienen a alguien dentro (autoritativo). Se aplica ANTES de tocar
    // los cuerpos: un slot que acaba de vaciarse se aparca fuera del campo, y
    // uno que acaba de ocuparse vuelve a colisionar, así la predicción local
    // simula la misma cancha que el servidor y no dibuja discos de relleno.
    this.applyActive(snap);

    // Posición del disco local que se ESTABA mostrando (predicha + offset actual),
    // para conservar continuidad visual tras la corrección.
    const w = this.game.world;
    const lb = this.localBody;
    const shownX = lb >= 0 ? w.px[lb] + this.errX : 0;
    const shownY = lb >= 0 ? w.py[lb] + this.errY : 0;
    // Igual para el BALÓN: lo que se ESTABA mostrando (predicho + offset).
    const ballIdx = this.game.ball;
    const shownBallX = w.px[ballIdx] + this.errBallX;
    const shownBallY = w.py[ballIdx] + this.errBallY;
    // Igual para cada RIVAL (lo que se mostraba = predicho + su offset actual).
    const shownPlayerX: number[] = [];
    const shownPlayerY: number[] = [];
    for (let i = 0; i < snap.players.length; i++) {
      const b = this.bodyOf(i);
      const has = b >= 0 && i !== this.localSlot;
      shownPlayerX[i] = has ? w.px[b] + (this.errPlayerX[i] ?? 0) : 0;
      shownPlayerY[i] = has ? w.py[b] + (this.errPlayerY[i] ?? 0) : 0;
    }

    // EXPERIMENTO: aceleración observada del rival entre snapshots (a = Δv/Δt).
    const snapDt = this.prevSnapTick >= 0
      ? (snap.serverTick - this.prevSnapTick) * NET_FIXED_DT : 0;
    for (let i = 0; i < snap.players.length; i++) {
      const b = this.bodyOf(i);
      if (b < 0) continue;
      if (i !== this.localSlot && snapDt > 0) {
        let ax = ((snap.players[i].vx ?? 0) - (this.prevVx[i] ?? 0)) / snapDt;
        let ay = ((snap.players[i].vy ?? 0) - (this.prevVy[i] ?? 0)) / snapDt;
        // Un choque o un saque producen Δv enormes que NO son "el rival
        // acelerando": se acotan a lo que puede dar el motor de un disco.
        const m = Math.hypot(ax, ay);
        if (m > PLAYER_ACCEL_FORCE) {
          ax = (ax / m) * PLAYER_ACCEL_FORCE;
          ay = (ay / m) * PLAYER_ACCEL_FORCE;
        }
        this.accX[i] = ax;
        this.accY[i] = ay;
      }
      this.prevVx[i] = snap.players[i].vx ?? 0;
      this.prevVy[i] = snap.players[i].vy ?? 0;
      // La velocidad autoritativa entra tal cual: es lo que hace avanzar al
      // rival por inercia hasta el siguiente snapshot (ver `applyLocal`).
      writeBody(this.game, b, snap.players[i]);
    }
    this.prevSnapTick = snap.serverTick;
    this.ticksSinceSnap = 0;
    if (!firstSync) this.detectRemoteKick(snap);
    writeBody(this.game, this.game.ball, snap.ball);
    this.game.score[0] = snap.score[0];
    this.game.score[1] = snap.score[1];
    if (snap.over !== undefined) {
      this.game.matchOver = snap.over;
      this.game.winner = snap.winner ?? null;
    }
    // Reloj autoritativo: fijarlo ANTES de re-simular pendientes evita que cada
    // snapshot vuelva a sumar el tiempo de los inputs ya predichos (el bug del
    // reloj "exponencial"). El replay lo adelanta desde esta base correcta.
    if (snap.matchTime !== undefined) this.game.matchTime = snap.matchTime;
    if (snap.resetTimer !== undefined) this.game.resetTimer = snap.resetTimer;
    if (snap.overtime !== undefined) this.game.overtime = snap.overtime;
    // Estado del saque: autoritativo, y SOLO autoritativo. El cliente lo aplica
    // igual que el servidor, así que su predicción respeta la misma regla.
    //
    // Si el servidor no manda el campo es que no conoce la regla: entonces el
    // círculo tampoco existe para él, y predecirlo en local sería inventarse una
    // restricción que nadie más aplica —el disco frenado aquí y libre allí, o
    // sea, goma elástica—. Sin dato, no hay saque.
    this.game.kickoffTeam = snap.kickoff ?? null;
    // CARGA de la patada: autoritativa, y se fija ANTES de re-simular los
    // inputs pendientes.
    //
    // Sin esto, reconciliar rebobinaba los cuerpos pero no la carga: el replay
    // volvía a sumar posesión sobre la que ya había, y el aro se llenaba mucho
    // más rápido de lo que el servidor contaba (medido: 7× con 100 ms de
    // latencia). El disparo salía flojo justo cuando el dibujo prometía "a
    // tope" — el jugador lo vive como "la pelota no sale con fuerza", y no le
    // falta razón: la fuerza que vio nunca existió.
    // Celebración del gol: autoritativa. Ver `Snapshot.goalAnim`.
    if (snap.goalAnim !== undefined) {
      this.game.goalAnim = snap.goalAnim;
      if (snap.goalSide !== undefined) this.game.goalSide = snap.goalSide;
    }
    if (snap.possession) {
      for (let i = 0; i < this.game.possession.length; i++) {
        const p = snap.possession[i];
        if (p === undefined) continue;
        this.game.possession[i] = p;
        this.game.charges[i] = p / POSSESSION_TIME_MAX;
        this.game.chargings[i] = p > 0;
      }
    }

    // Sincroniza el reloj local para liderar al servidor (ver más abajo). Debe
    // ocurrir ANTES de replayar pendientes, porque puede cambiar `this.tick` y
    // reencolar/descartar pendientes.
    // ¿Está el cliente jugando de verdad (prediciendo y enviando) desde el
    // snapshot anterior? Si no, las señales de red que trae este snapshot
    // describen nuestra ausencia, no la red.
    const running = this.predictCount > this.predictCountAtLastSnap;
    this.predictCountAtLastSnap = this.predictCount;
    if (this.autoLead) this.syncClock(snap, firstSync, running);

    this.pending = this.pending.filter((p) => p.tick > snap.ackTick);
    for (const p of this.pending) this.applyLocal(p.input);

    // Position error smoothing: tras fijar autoritativo + replay, el disco local
    // quedó en su nueva posición predicha. El error = lo que se mostraba − lo
    // nuevo; se guarda como offset que se reabsorbe, en vez de saltar. El techo
    // de lo "suavizable" escala con la velocidad del cuerpo (ver
    // `SMOOTH_SPEED_WINDOW`): así una corrección rápida pero esperable se
    // resbala, y solo el teletransporte real (saque tras gol) se aplica de golpe.
    if (lb >= 0 && !firstSync) {
      const dx = shownX - w.px[lb];
      const dy = shownY - w.py[lb];
      const cap = smoothCap(SMOOTH_BASE_LOCAL, bodySpeed(snap.players[this.localSlot] ?? snap.ball));
      if (Math.hypot(dx, dy) <= cap) {
        this.errX = dx;
        this.errY = dy;
      } else {
        this.errX = 0;
        this.errY = 0;
      }
    } else if (firstSync) {
      this.errX = 0;
      this.errY = 0;
    }

    // Mismo suavizado para el balón. Es el que más lo necesita: una patada del
    // rival no se puede predecir, y el balón sale a cientos de píxeles por
    // décima de segundo, así que la corrección legítima es enorme.
    if (!firstSync) {
      const dbx = shownBallX - w.px[ballIdx];
      const dby = shownBallY - w.py[ballIdx];
      // Si la pelota la estás moviendo TÚ, el techo se amplía: esa corrección es
      // el desfase de la red sobre una jugada que ya predijimos bien, no una
      // jugada distinta (ver `SMOOTH_LOCAL_TOUCH_FACTOR`).
      const touchK = this.localTouchTicks > 0 ? SMOOTH_LOCAL_TOUCH_FACTOR : 1;
      if (Math.hypot(dbx, dby) <= smoothCap(SMOOTH_BASE_BALL, bodySpeed(snap.ball)) * touchK) {
        this.errBallX = dbx;
        this.errBallY = dby;
      } else {
        this.errBallX = 0;
        this.errBallY = 0;
      }
    } else {
      this.errBallX = 0;
      this.errBallY = 0;
    }

    // Mismo suavizado para cada RIVAL: se dibuja en su posición PREDICHA (coasting
    // al presente) + offset que decae, en vez de interpolarlo en el pasado. Así la
    // pelota interactúa con el rival DONDE SE VE (su cuerpo de colisión ya coastea
    // al presente en la predicción). Cambios bruscos por encima del cap = de golpe.
    for (let i = 0; i < snap.players.length; i++) {
      if (i === this.localSlot) continue;
      const b = this.bodyOf(i);
      if (b < 0) continue;
      if (firstSync) { this.errPlayerX[i] = 0; this.errPlayerY[i] = 0; continue; }
      const dx = shownPlayerX[i] - w.px[b];
      const dy = shownPlayerY[i] - w.py[b];
      if (Math.hypot(dx, dy) <= smoothCap(SMOOTH_BASE_PLAYER, bodySpeed(snap.players[i]))) {
        this.errPlayerX[i] = dx;
        this.errPlayerY[i] = dy;
      } else {
        this.errPlayerX[i] = 0;
        this.errPlayerY[i] = 0;
      }
    }

    // Estimación de RTT para el visualizador de ping: edad del último input
    // confirmado (this.tick − ackTick), descontando el intervalo de snapshot
    // (que es muestreo, no latencia). No autoritativo; solo telemetría.
    //
    // Solo cuenta si el ack corresponde a un input enviado DESPUÉS del último
    // salto de reloj. Cuando el bucle de render se detiene —pestaña en segundo
    // plano, que es el caso habitual: el navegador congela `requestAnimationFrame`—
    // el cliente deja de predecir y de enviar inputs, pero los snapshots siguen
    // llegando por el socket. El reloj local salta hacia adelante para seguir al
    // servidor mientras el ack se queda anclado en el último input enviado, así
    // que `tick − ackTick` crece sin parar y el ping se dispara a valores
    // absurdos que además envenenan el máximo de la sesión. Esa diferencia no es
    // latencia: es que no había nada que medir.
    if (snap.ackTick > this.lastAckSeen) {
      this.lastAckSeen = snap.ackTick;
      this.ticksSinceAck = 0;
    }
    if (running && snap.ackTick >= this.rttValidFromTick) {
      const rt = Math.max(0, (this.tick - snap.ackTick) - SNAPSHOT_INTERVAL_TICKS);
      if (!this.rttSynced) { this.rttTicksEwma = rt; this.rttSynced = true; }
      else this.rttTicksEwma += (rt - this.rttTicksEwma) * RTT_EWMA;
      const ms = this.rttTicksEwma * MS_PER_TICK;
      if (ms > this.rttMaxMs) this.rttMaxMs = ms;
      // ESTABILIDAD de la conexión, medida aparte de la latencia.
      //
      // Son dos cosas distintas y solo una tiene arreglo: 110 ms constantes se
      // juegan igual que una LAN (medido), y 110 ms que saltan a 360 cada rato
      // son tirones. Un jugador que solo ve "110 ms" no puede distinguir "vivo
      // lejos" de "mi Wi-Fi va mal", y el segundo caso sí se arregla — con un
      // cable. Por eso se mide con memoria del pico: una media diría que todo va
      // bien justo cuando el bache acaba de pasar.
      this.rttJitter.add(rt);
    }

    this.snapBuffer.push(snap);
    if (this.snapBuffer.length > MAX_SNAP_BUFFER) this.snapBuffer.shift();
  }

  /**
   * Adopta el estado de PARTIDA de un snapshot (marcador, fin, reloj, prórroga)
   * sin tocar los cuerpos ni la reconciliación.
   *
   * Es lo único que puede cambiar mientras el reloj del servidor está parado
   * (sala en pausa o terminada), y por eso vive separado del camino normal.
   */
  private adoptMatchState(snap: Snapshot): void {
    this.game.score[0] = snap.score[0];
    this.game.score[1] = snap.score[1];
    if (snap.over !== undefined) {
      this.game.matchOver = snap.over;
      this.game.winner = snap.winner ?? null;
    }
    if (snap.matchTime !== undefined) this.game.matchTime = snap.matchTime;
    if (snap.overtime !== undefined) this.game.overtime = snap.overtime;
    this.game.kickoffTeam = snap.kickoff ?? null;
  }

  /**
   * Patadas de rivales pendientes (sonido y partículas). Drena la cola: quien
   * llama las consume una vez y no se repiten en el frame siguiente.
   */
  takeRemoteKicks(): RemoteKick[] {
    if (this.remoteKicks.length === 0) return EMPTY_KICKS;
    const out = this.remoteKicks;
    this.remoteKicks = [];
    return out;
  }

  /**
   * ¿Trae este snapshot una patada de un RIVAL?
   *
   * El cliente no simula las teclas de los demás, así que su patada no está en
   * la predicción: aparece como un salto de velocidad del balón entre lo que
   * teníamos y lo autoritativo. Se atribuye a un rival solo si además había uno
   * al alcance del balón — así un saque tras gol, un rebote fuerte o una
   * corrección de red no acaban sonando como una patada que nadie dio.
   *
   * Sin esto, el juego enmudecía en cuanto la jugada era del otro: tú pateabas y
   * sonaba, el rival pateaba y no. La mitad del partido pasaba en silencio.
   */
  private detectRemoteKick(snap: Snapshot): void {
    const w = this.game.world;
    const b = this.game.ball;
    const predVx = w.vx[b];
    const predVy = w.vy[b];
    const dvx = snap.ball.vx - predVx;
    const dvy = snap.ball.vy - predVy;
    const delta = Math.hypot(dvx, dvy);
    if (delta < REMOTE_KICK_DELTA) return;

    // ¿Qué rival la tenía al alcance en el momento que describe el snapshot?
    let bestSlot = -1;
    let bestDist = Infinity;
    for (let i = 0; i < snap.players.length; i++) {
      if (i === this.localSlot) continue;
      if (this.game.active[i] === false) continue;
      const p = snap.players[i];
      if (!p) continue;
      const d = Math.hypot(snap.ball.x - p.x, snap.ball.y - p.y);
      if (d < bestDist) { bestDist = d; bestSlot = i; }
    }
    if (bestSlot < 0 || bestDist > POSSESSION_RANGE + REMOTE_KICK_RANGE_PAD) return;

    const nx = delta > 1e-9 ? dvx / delta : 1;
    const ny = delta > 1e-9 ? dvy / delta : 0;
    this.remoteKicks.push({
      slot: bestSlot,
      x: snap.ball.x - nx * BALL_R,
      y: snap.ball.y - ny * BALL_R,
      nx,
      ny,
      // Misma escala que `KickInfo.intensity` del sim local (0,4 el toque
      // mínimo, 1 el saque a plena carga), para que suene y chispee igual.
      intensity: Math.max(0.4, Math.min(1, delta / (KICK_SPEED_MIN * 3))),
    });
    // El pulso del anillo es estado de render del disco que patea: puesto aquí,
    // el rival "acusa" su propia patada igual que el jugador local.
    this.game.kickPulses[bestSlot] = 1;
  }

  /**
   * Reloj adaptativo. Objetivo: mantener `bufAhead` (lead observado por el
   * servidor) en un valor pequeño y positivo (`TARGET_BUFFER` + jitter), para que
   * los inputs lleguen justo a tiempo sin sobre-latencia. Corrige el reloj de
   * forma GRADUAL (±1 tick por snapshot) salvo derivas grandes.
   */
  private syncClock(snap: Snapshot, firstSync: boolean, running: boolean): void {
    // Jitter de llegada de snapshots (para el delay de interpolación adaptativo):
    // varianza del desfase entre el reloj local y el serverTick recibido.
    const offset = this.tick - snap.serverTick;
    if (!this.syncedOffset) { this.offsetEwma = offset; this.syncedOffset = true; }
    else {
      this.offsetJitterEwma += (Math.abs(offset - this.offsetEwma) - this.offsetJitterEwma) * 0.1;
      this.offsetEwma += (offset - this.offsetEwma) * 0.1;
    }

    // Sin `bufAhead` (snapshots de test hechos a mano) caemos al lead fijo previo:
    // salto sólo hacia adelante, nunca retrocede.
    if (snap.bufAhead === undefined) {
      const target = snap.serverTick + INITIAL_LEAD;
      if (this.tick < target) {
        this.pending = this.pending.filter((p) => p.tick > snap.serverTick);
        this.tick = target;
        this.rttValidFromTick = target; // el salto invalida la medida de ping
      }
      return;
    }

    // Controlador del jitter-buffer: adapta `leadTarget` según la señal bufAhead.
    //
    // Solo se alimenta mientras el cliente está jugando. Sin ese filtro, una
    // pestaña en segundo plano lo rompe: el cliente deja de enviar,
    // `bufAhead` se desploma (el servidor no ve inputs por delante) y el
    // controlador lo interpreta como "voy corto de buffer" y empuja el lead
    // hasta el máximo. Al volver, el jugador se encuentra corriendo 300 ms por
    // delante del servidor sin motivo — más lag efectivo contra el rival y un
    // ping que marca ese lead como si fuera latencia de red. No es jitter: es
    // que no había nada que medir.
    if (!running) {
      // El cliente no está jugando: lo que diga `bufAhead` describe su ausencia.
      this.bufControlStale = true;
    } else if (this.bufControlStale) {
      // Ha vuelto, pero el servidor todavía puede no haber visto sus inputs
      // nuevos. En cuanto confirme uno, el controlador arranca de cero en vez de
      // seguir promediando el hueco.
      if (snap.ackTick >= this.rttValidFromTick) {
        this.bufControlStale = false;
        this.bufAheadEwma = TARGET_BUFFER;
        this.bufJitterEwma = 0;
      }
    } else {
      const sample = snap.bufAhead;
      this.bufJitterEwma += (Math.abs(sample - this.bufAheadEwma) - this.bufJitterEwma) * 0.1;
      this.bufAheadEwma += (sample - this.bufAheadEwma) * 0.1;
      const desiredBuffer = TARGET_BUFFER + Math.ceil(this.bufJitterEwma);
      // Si el servidor ve menos buffer del deseado, hay que adelantarse más.
      this.leadTarget += (desiredBuffer - this.bufAheadEwma) * 0.05;
      this.leadTarget = Math.max(MIN_LEAD, Math.min(MAX_LEAD, this.leadTarget));
    }

    // Recuperación: el servidor no confirma NI UN input. Puede que lleguemos
    // tarde o demasiado pronto, y no hay forma de distinguirlo desde aquí, así
    // que se barre la ventana en pasos en vez de insistir en una sola dirección.
    if (running && this.ticksSinceAck > INPUT_STALL_TICKS
        && this.ticksSinceAck % RECOVERY_INTERVAL_TICKS === 0) {
      const steps = 4;
      this.recoveryStep = (this.recoveryStep + 1) % steps;
      const span = MAX_LEAD - MIN_LEAD;
      this.leadTarget = MIN_LEAD + (span * (steps - 1 - this.recoveryStep)) / (steps - 1);
      this.bufAheadEwma = TARGET_BUFFER;
      this.bufJitterEwma = 0;
    }

    const desiredTick = snap.serverTick + Math.round(this.leadTarget);
    const drift = desiredTick - this.tick;

    if (firstSync || Math.abs(drift) > CLOCK_SNAP_THRESHOLD) {
      // Join o hitch: salta directo (con smoothing del error, no se ve el brinco
      // del disco local; el rival/balón van interpolados). El ping deja de
      // muestrearse hasta que el servidor confirme un input POSTERIOR al salto:
      // hasta entonces, la edad del ack mide el salto, no la latencia.
      this.tick = desiredTick;
      this.rttValidFromTick = desiredTick;
      this.pending = this.pending.filter((p) => p.tick > snap.serverTick);
    } else if (drift >= 1) {
      this.tick += 1; // adelantar 1 tick (más buffer)
    } else if (drift <= -1) {
      // Atrasar 1 tick, sin bajar de un lead mínimo seguro sobre el servidor.
      if (this.tick - 1 >= snap.serverTick + MIN_LEAD) {
        this.tick -= 1;
        this.pending = this.pending.filter((p) => p.tick < this.tick);
      }
    }
  }

  /**
   * Delay de interpolación recomendado (ticks) para las entidades renderizadas
   * en el pasado (rivales). Arranca en `INTERP_MIN_DELAY` (casi presente) y crece
   * con el jitter medido: red estable ⇒ rivales "pegados" al presente; red con
   * jitter ⇒ más buffer para no reintroducir tirones.
   */
  get interpDelayTicks(): number {
    const jitter = this.syncedOffset ? this.offsetJitterEwma : 0;
    const d = INTERP_MIN_DELAY + Math.ceil(jitter * 1.5) + this.interpBias;
    // Con sesgo negativo el suelo baja a 0 (todo extrapolado): es lo que pide
    // quien prefiere ver al rival en el presente aunque a veces se corrija.
    return Math.max(0, Math.min(INTERP_MAX_DELAY, d));
  }

  /** Interpola el balón para un `renderTick` (unos ticks en el pasado). */
  interpolateBall(renderTick: number): BodyState | null {
    return this.interpolate(renderTick, (s) => s.ball);
  }

  /** Interpola el cuerpo de un jugador (útil para el rival remoto). */
  interpolatePlayer(slot: number, renderTick: number): BodyState | null {
    return this.interpolate(renderTick, (s) => s.players[slot] ?? s.players[0]);
  }

  private interpolate(renderTick: number, pick: (s: Snapshot) => BodyState): BodyState | null {
    const buf = this.snapBuffer;
    if (buf.length === 0) return null;
    if (renderTick <= buf[0].serverTick) return pick(buf[0]);
    for (let i = 0; i < buf.length - 1; i++) {
      const a = buf[i];
      const b = buf[i + 1];
      if (renderTick >= a.serverTick && renderTick <= b.serverTick) {
        const span = b.serverTick - a.serverTick;
        const t = span > 0 ? (renderTick - a.serverTick) / span : 0;
        return lerpBody(pick(a), pick(b), t);
      }
    }
    // El renderTick va por delante del último snapshot (buffer agotado por jitter
    // o pérdida): extrapola desde el último estado usando su velocidad, en vez de
    // congelar/teletransportar la entidad. Se limita a `MAX_EXTRAP_TICKS`.
    const last = pick(buf[buf.length - 1]);
    const ahead = Math.min(MAX_EXTRAP_TICKS, renderTick - buf[buf.length - 1].serverTick);
    if (ahead <= 0) return last;
    const dt = ahead * NET_FIXED_DT;
    return { x: last.x + last.vx * dt, y: last.y + last.vy * dt, vx: last.vx, vy: last.vy };
  }
}
